import is from '@sindresorhus/is';

import type { JobProfileVersion } from '~/shared/data-contract/job-profile-versions';

import type { JobItemDetails } from '../../api/use-job-item-details';
import type { AuditActor, AuditTableStatus, AuditTrailStep } from './types';

const toAuditActor = (
    user: { readonly firstName?: string; readonly lastName?: string } | null | undefined,
): AuditActor | null => {
    if (is.nullOrUndefined(user)) {
        return null;
    }
    if (!is.nonEmptyString(user.firstName) && !is.nonEmptyString(user.lastName)) {
        return null;
    }
    return { firstName: user.firstName ?? '', lastName: user.lastName ?? '' };
};

const stageIndex = (status: AuditTableStatus): 0 | 1 | 2 => {
    if (status === 'draft') {
        return 0;
    }
    if (status === 'reviewed') {
        return 1;
    }
    return 2;
};

export const deriveStatus = (currentVersion: JobProfileVersion): AuditTableStatus => {
    if (currentVersion.publishedAt !== null) {
        return 'published';
    }
    if (currentVersion.reviewedAt !== null) {
        return 'reviewed';
    }
    return 'draft';
};

export const buildAuditTrailSteps = (
    status: AuditTableStatus,
    currentVersion: JobProfileVersion,
    item: JobItemDetails,
): readonly AuditTrailStep[] => {
    const current = stageIndex(status);
    const draftedActor = toAuditActor(currentVersion.createdBy ?? item.creator ?? null);
    const reviewerActor = toAuditActor(currentVersion.reviewer ?? item.reviewer ?? null);
    const publisherActor = toAuditActor(currentVersion.publishedBy) ?? reviewerActor;

    return [
        {
            id: 'draft',
            state: current > 0 ? 'done' : 'current',
            actor: draftedActor,
            at: currentVersion.createdAt,
        },
        {
            id: 'reviewed',
            state: current > 1 ? 'done' : current === 1 ? 'current' : 'upcoming',
            actor: reviewerActor,
            at: currentVersion.reviewedAt,
        },
        {
            id: 'published',
            state: current === 2 ? 'current' : 'upcoming',
            actor: status === 'published' ? publisherActor : null,
            at: status === 'published' ? currentVersion.publishedAt : null,
        },
    ];
};
